// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer icon component module for displaying customer logos or fallback icons.
 * Provides a consistent way to show customer visual identification across the application.
 * Uses CompanyLogo internally with logo API integration for automatic logo lookup.
 */
import { Customer } from '@/customer/api/types';
import { CompanyLogo } from '@/customer/components/company-logo';

/**
 * Props for the CustomerIcon component
 */
interface Props {
  /**
   * The customer data containing logo URL, domain, and name information.
   */
  customer: Customer;

  /**
   * Optional size in pixels for the icon.
   * @default 32
   */
  size?: number;
}

/**
 * Default size in pixels for the customer icon
 */
const DEFAULT_SIZE = 32;

/**
 * A component that displays a customer's logo with fallback options.
 *
 * Logo resolution priority:
 * 1. Manual upload (logoFileKey) - highest priority
 * 2. Logo API lookup using customer domain
 * 3. Initials avatar with neutral gray background - final fallback
 *
 * Features:
 * - Displays customer logo if available (manual upload or logo API)
 * - Handles image loading errors gracefully
 * - Falls back to initials avatar
 * - Shows tooltip with customer name
 * - Configurable size
 * - Prevents layout shifts during loading
 *
 * @example
 * ```tsx
 * // Basic usage
 * <CustomerIcon customer={customerData} />
 *
 * // Custom size
 * <CustomerIcon
 *   customer={customerData}
 *   size={64}
 * />
 * ```
 */
export function CustomerIcon(props: Props) {
  const size = props.size ?? DEFAULT_SIZE;

  // Map the numeric size to CompanyLogo's size prop
  const logoSize = size > 40 ? 'medium' : 'small';

  return (
    <CompanyLogo
      domain={props.customer.domain}
      name={props.customer.name}
      size={logoSize}
      manualLogoUrl={props.customer.logoFileKey}
    />
  );
}
